/**
 * Language Switcher Component
 *
 * Dropdown menu for selecting application language with:
 * - Flag icons for each language
 * - Keyboard navigation support
 * - Screen reader accessibility
 * - RTL support for Arabic
 * - Persistent language preference
 *
 * @module components/ui/LanguageSwitcher
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Globe } from "lucide-react";

import { Button } from "@shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import { languages, changeLanguage, getCurrentLanguage, isRTL } from "@shared/i18n";
import { cn } from "@shared/lib/utils";

// =============================================================================
// TYPES
// =============================================================================

export interface LanguageSwitcherProps {
  /** Additional CSS classes */
  className?: string;
  /** Show language names instead of flags */
  showNames?: boolean;
  /** Compact display mode */
  compact?: boolean;
  /** Callback when language changes */
  onLanguageChange?: (language: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Language switcher dropdown with flag icons and accessibility features
 *
 * @example
 * ```tsx
 * <LanguageSwitcher
 *   onLanguageChange={(lang) => console.log('Language changed to', lang)}
 *   className="ms-4"
 * />
 * ```
 */
export function LanguageSwitcher({
  className,
  showNames = false,
  compact = false,
  onLanguageChange,
}: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const currentLang = getCurrentLanguage();
  const currentLangInfo = languages[currentLang];
  const isCurrentRTL = isRTL(currentLang);

  /**
   * Handle language change
   */
  const handleLanguageChange = async (lang: string) => {
    try {
      await changeLanguage(lang as keyof typeof languages);
      onLanguageChange?.(lang);
    } catch (error) {
      console.error("[LanguageSwitcher] Failed to change language:", error);
    }
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      // Trigger dropdown open/close
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? "sm" : "default"}
          className={cn(
            "gap-2 data-[state=open]:bg-accent",
            isCurrentRTL && "flex-row-reverse",
            className
          )}
          aria-label={t("language.select")}
          onKeyDown={handleKeyDown}
        >
          {compact ? (
            <Globe className="h-4 w-4" aria-hidden="true" />
          ) : (
            <>
              <span className="text-lg" aria-hidden="true">
                {currentLangInfo.flag}
              </span>
              <span className="hidden sm:inline">
                {showNames ? currentLangInfo.name : t("language.select")}
              </span>
            </>
          )}
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="min-w-[160px]"
        sideOffset={8}
      >
        {Object.entries(languages).map(([code, info]) => {
          const isActive = code === currentLang;
          const isLangRTL = isRTL(code);

          return (
            <DropdownMenuItem
              key={code}
              onClick={() => handleLanguageChange(code)}
              className={cn(
                "gap-2 cursor-pointer",
                isActive && "bg-accent",
                isLangRTL && "flex-row-reverse justify-end"
              )}
              aria-selected={isActive}
              role="option"
            >
              <span className="text-lg" aria-hidden="true">
                {info.flag}
              </span>
              <span className="flex-1">{info.name}</span>
              {isActive && (
                <span className="text-xs text-muted-foreground ms-auto">
                  ✓
                </span>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSwitcher;
